import { useEffect, useState, RefObject } from 'react';

/**
 * Hook to track caption visibility by monitoring caption content using MutationObserver
 * @param wrapperRef - Ref to the wrapper element containing the captions
 * @param source - Media source (used as dependency)
 * @param showClosedCaptions - Whether closed captions should be displayed
 * @returns Boolean indicating if captions have visible content
 */
export const useCaptionVisibility = (
  wrapperRef: RefObject<HTMLElement | null>,
  source: string | null,
  showClosedCaptions: boolean
): boolean => {
  const [captionsVisible, setCaptionsVisible] = useState(false);

  useEffect(() => {
    const checkCaptionContent = () => {
      if (wrapperRef.current) {
        const captionElement =
          wrapperRef.current.querySelector('.vds-captions');
        if (captionElement) {
          const hasContent =
            captionElement.textContent &&
            captionElement.textContent.trim().length > 0;
          setCaptionsVisible(hasContent || false);
        } else {
          setCaptionsVisible(false);
        }
      }
    };

    // Check initially
    checkCaptionContent();

    // Use MutationObserver to watch for caption content changes
    const observer = new MutationObserver(checkCaptionContent);

    // Observe the wrapper for caption changes
    if (wrapperRef.current) {
      observer.observe(wrapperRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => observer.disconnect();
  }, [source, showClosedCaptions, wrapperRef]);

  return captionsVisible;
};
